// leadContact — validation for the funnel's first step.
//
// Pure. No React, no fetching: the panel renders, this decides what is valid.
//
// The shared `LeadContactRequestSchema` (merged from the credit step's
// `CreditContactSchema`) stays the arbiter of what the WIRE accepts, and is
// still run last as a backstop. The rules here are STRICTER, and per field,
// because the wire rules alone let real garbage through: "e2334123131323123"
// stripped to its first ten digits is a "valid" phone, and a first name of
// "123" is "non-empty". A shopper should be told at the field, in plain words,
// what is wrong — not have the payload silently mangled into something valid.

import { LeadContactRequestSchema, type LeadContactRequest } from '@rheora/enrollment-contract';

/** Field-keyed messages; empty object means the form may be submitted. */
export type LeadContactErrors = Partial<Record<keyof LeadContactRequest, string>>;

export interface LeadContactDraft {
  readonly firstName: string;
  readonly lastName: string;
  readonly email: string;
  /** As typed — separators and all. Stripped to 10 digits before validation. */
  readonly phone: string;
}

export const EMPTY_LEAD_CONTACT: LeadContactDraft = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
};

/** Letters (any script, with accents), then letters/marks, spaces, ' - . */
const NAME_RE = /^\p{L}[\p{L}\p{M}'’ .-]*$/u;
const NAME_MAX = 50;

/** Digits and the punctuation people type around them — nothing else. */
const PHONE_CHARS_RE = /^\+?[\d\s().-]+$/u;
/** NANP: area code and exchange each start 2–9. */
const NANP_RE = /^[2-9]\d{2}[2-9]\d{6}$/u;

/** A real address shape: one @, a domain with a dot, no spaces. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/u;
const EMAIL_MAX = 254;

/**
 * Strip a typed phone to the NANP digits the wire wants.
 *
 * People type `(214) 555-0134`, `214-555-0134` and `+1 214 555 0134`, and all
 * three are the same number. A leading country `1` is dropped, since an
 * 11-digit string beginning with 1 is the same NANP number. Nothing else is
 * dropped: extra digits make the number INVALID, they are not trimmed away.
 */
export function normalisePhone(raw: string): string {
  const digits = raw.replace(/\D/gu, '');
  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
}

/** True for ten digits a US carrier could actually issue. */
export function isNanpNumber(digits: string): boolean {
  return NANP_RE.test(digits);
}

/** Ten digits at most, in the three groups people read them in: `214 555 0134`. */
export function formatPhoneDigits(digits: string): string {
  const d = digits.slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)} ${d.slice(3)}`;
  return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`;
}

/**
 * What the phone field holds after a keystroke or a paste: digits only, at
 * most ten, grouped `214 555 0134`. The field ENFORCES the shape as it is
 * typed — a letter is ignored and an eleventh digit never lands — instead of
 * accepting anything and objecting on blur, which is how a fifteen-digit
 * string sat in the field looking accepted.
 *
 * The country code is fixed in the field's own +1 prefix, so a `+1` that
 * arrives with a PASTED or autofilled number (`+1 214 555 0134`) is dropped.
 * `previous` is what the field held before the change: a jump of more than
 * one digit is a paste, one digit is typing. Only a paste gets the leading-1
 * treatment. Someone TYPING `1` as an area code is making a mistake, and the
 * number they typed stays on screen so the blur message ("Enter a valid US
 * phone number") points at something they can see, rather than the first
 * digit silently vanishing under their cursor.
 */
export function formatPhoneInput(raw: string, previous = ''): string {
  let digits = raw.replace(/\D/gu, '');
  const pasted = digits.length - previous.replace(/\D/gu, '').length > 1;
  if (pasted && digits.length >= 11 && digits.startsWith('1')) digits = digits.slice(1);
  return formatPhoneDigits(digits);
}

export function nameError(raw: string, which: 'first' | 'last'): string | undefined {
  const value = raw.trim();
  const label = which === 'first' ? 'first name' : 'last name';
  if (value === '') return `Enter your ${label}.`;
  if (value.length < 2) return `Your ${label} needs at least 2 letters.`;
  if (value.length > NAME_MAX) return `Your ${label} can be at most ${NAME_MAX} characters.`;
  if (!NAME_RE.test(value)) return `Your ${label} can only contain letters, spaces, hyphens and apostrophes.`;
  return undefined;
}

export function emailError(raw: string): string | undefined {
  const value = raw.trim();
  if (value === '') return 'Enter your email address.';
  if (value.length > EMAIL_MAX || !EMAIL_RE.test(value)) {
    return 'Enter a valid email address, like you@example.com.';
  }
  return undefined;
}

export function phoneError(raw: string): string | undefined {
  const value = raw.trim();
  if (value === '') return 'Enter your phone number.';
  if (!PHONE_CHARS_RE.test(value)) return 'Phone numbers can only contain digits.';
  const digits = normalisePhone(value);
  if (digits.length !== 10) return 'Enter a 10-digit phone number.';
  if (!NANP_RE.test(digits)) return 'Enter a valid US phone number.';
  return undefined;
}

/** One field's message, for validation as the shopper leaves the field. */
export function validateLeadContactField(
  field: keyof LeadContactDraft,
  draft: LeadContactDraft,
): string | undefined {
  switch (field) {
    case 'firstName':
      return nameError(draft.firstName, 'first');
    case 'lastName':
      return nameError(draft.lastName, 'last');
    case 'email':
      return emailError(draft.email);
    case 'phone':
      return phoneError(draft.phone);
  }
}

/** The payload for `POST /bff/contact`, built from what was typed. */
export function toLeadContactRequest(draft: LeadContactDraft): LeadContactRequest {
  return {
    firstName: draft.firstName.trim(),
    lastName: draft.lastName.trim(),
    email: draft.email.trim(),
    phone: normalisePhone(draft.phone),
  };
}

/**
 * Validate a draft, returning one message per bad field.
 *
 * Messages are written for the person filling the form, not for the schema:
 * "Enter a 10-digit phone number" tells them what to do, where Zod's
 * "phone must be 10 digits" reads like a rule being quoted at them.
 */
export function validateLeadContact(draft: LeadContactDraft): LeadContactErrors {
  const errors: LeadContactErrors = {};
  for (const field of ['firstName', 'lastName', 'email', 'phone'] as const) {
    const message = validateLeadContactField(field, draft);
    if (message !== undefined) errors[field] = message;
  }

  // The wire's own rules, as a backstop — anything above should already have
  // caught it, but the shared schema is what the BFF will actually enforce.
  const parsed = LeadContactRequestSchema.safeParse(toLeadContactRequest(draft));
  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      const field = issue.path[0];
      if (field === 'email' && errors.email === undefined) {
        errors.email = 'Enter a valid email address, like you@example.com.';
      }
      if (field === 'phone' && errors.phone === undefined) {
        errors.phone = 'Enter a 10-digit phone number.';
      }
      if (field === 'firstName' && errors.firstName === undefined) {
        errors.firstName = 'Enter your first name.';
      }
      if (field === 'lastName' && errors.lastName === undefined) {
        errors.lastName = 'Enter your last name.';
      }
    }
  }
  return errors;
}

/** True when nothing is wrong with the draft. */
export function isLeadContactValid(draft: LeadContactDraft): boolean {
  return Object.keys(validateLeadContact(draft)).length === 0;
}
